import { Component, OnInit } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import {
  IActiveUsersByFeatureInfo,
  IDashboardApiData,
  IDashboardApiResponse,
  IMonthlyOrdersByFeatureInfo,
} from "@models/dashboard-api.model";
import { AppStateService } from "@services/app-state/app-state.service";
import { RequestService } from "@services/request/request.service";
import { Chart, ChartConfiguration, ChartData, ChartType } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { BaseChartDirective } from "ng2-charts";
import { scrollWindow } from "./scroll-window.plugin";

// Register once at module load — before any chart is instantiated
Chart.register(ChartDataLabels);

/** One (period, country) reading, flattened out of whichever payload it came from. */
interface CountryRow {
  /** Sortable period key, e.g. "2026-09-14" or "2026-09". */
  dt: string;
  /** What the x-axis shows, e.g. "14 Sep" or "Sep 2026". */
  label: string;
  iso2: string;
  value: number;
}

/**
 * A stacked bar chart split by country, with its own country filter.
 *
 * `rows` is everything the API returned; `selected` is what the reader has
 * ticked. The chart is rebuilt from the two whenever either changes, so
 * unticking a country genuinely removes it from the stack and from the total
 * printed above the bar - rather than only hiding a legend entry, which is what
 * clicking the legend does and is not the same thing.
 */
interface CountryChart {
  key: string;
  title: string;
  subtitle: string;
  /** Y-axis title. */
  axis: string;
  /** Formats values as money. */
  money: boolean;
  /**
   * An order series: the API already restricts it to the page's one selected
   * market (amounts are in that market's currency and must never be summed
   * across markets), so it has no per-chart country filter.
   */
  market: boolean;
  rows: CountryRow[];
  /** Every country in `rows`, in the order the checkboxes show them. */
  countries: string[];
  selected: Set<string>;
  data: ChartData;
  options: ChartConfiguration["options"];
}

/**
 * The order/sales charts are switched between live markets (Country > Market
 * launch). Exactly one is shown at a time: SGD, USD and MYR order values cannot
 * be added together.
 */
type DashboardMarket = string;

@Component({
  selector: "fh-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.css"],
  standalone: true,
  imports: [BaseChartDirective, RefreshButtonComponent],
})
export class DashboardComponent implements OnInit {
  // Chart data - combo charts (mixed type)
  dailySearchesData: ChartData = { labels: [], datasets: [] };
  monthlySearchesData: ChartData = { labels: [], datasets: [] };
  dailyCartData: ChartData = { labels: [], datasets: [] };
  monthlyCartData: ChartData = { labels: [], datasets: [] };
  monthlyOrdersByFeatureData: ChartData = { labels: [], datasets: [] };
  activeUsersByFeatureData: ChartData = { labels: [], datasets: [] };

  // Chart type
  barChartType: ChartType = "bar";

  // Chart options with dual y-axes for combo charts (datalabels disabled)
  chartOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
      },
      // Disable datalabels on all non-orders charts
      datalabels: { display: false },
    } as any,
    scales: {
      y: {
        type: "linear" as const,
        display: true,
        position: "left" as const,
        beginAtZero: true,
        title: {
          display: true,
          text: "Orders / Users",
        },
      },
      y1: {
        type: "linear" as const,
        display: true,
        position: "right" as const,
        beginAtZero: true,
        title: {
          display: true,
          text: "Sales ($)",
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  // Stacked bar: monthly total orders segmented by the feature that drove them.
  // Segments stack to the month's total confirmed orders.
  stackedOrdersOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: "top" as const,
      },
      // Too many small segments to label each; rely on the tooltip instead.
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          footer: (items: any[]) => {
            const total = items.reduce((sum, it) => sum + (it.parsed?.y ?? 0), 0);
            return `Total orders: ${total.toLocaleString()}`;
          },
        },
      },
    } as any,
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        type: "linear" as const,
        beginAtZero: true,
        title: { display: true, text: "Orders" },
      },
    },
  };

  // Horizontal bar: current-month active users per feature, most-used first.
  activeUsersByFeatureOptions: ChartConfiguration["options"] = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: "end" as const,
        align: "right" as const,
        color: "#0c55b8",
        font: { size: 10, weight: "bold" as const },
        formatter: (v: number) => (v > 0 ? v.toLocaleString() : ""),
      },
    } as any,
    layout: { padding: { right: 28 } },
    scales: {
      x: { beginAtZero: true, title: { display: true, text: "Active users" } },
      y: { ticks: { autoSkip: false } },
    },
  };

  /** Feature key → human label for the active-users chart. */
  private readonly FEATURE_LABELS: Record<string, string> = {
    search_bar: "Search Bar",
    visual_search: "Search by Photo",
    filter_page: "Filter Page",
    wizard: "Wizard",
    ph_section: "PH Section",
    personalized_section: "Personalized Section",
    smart_bundles: "Smart Bundles",
    starter_packs: "Starter Packs",
    new_fishes: "New Fishes",
    popular_fish_species: "Popular Fish Species",
    categories: "Categories",
    merchant_shop_pages: "Merchant Shop Pages",
    what_others_viewing: "What Others Are Viewing",
    new_plants: "New Plants",
    recently_purchased: "Recently Purchased",
    fishyhub_ai: "FishyHub AI (Cart)",
    fishyhub_ai_product: "FishyHub AI (Product Page)",
    fishyhub_ai_chats: "FishyHub AI (Chats Page)",
    delivery_optimizer: "Delivery Optimizer",
    wishlist: "Wishlist",
    you_might_also_like: "You Might Also Like",
    more_from_merchant: "More From This Merchant",
    feed: "Feed",
    reels: "Reels",
  };

  // Fixed display order + palette for the attribution segments. Keys match the
  // backend `feature` values; "multiple" and "others" are always last.
  private readonly ORDER_FEATURES: Array<{ key: string; label: string; color: string }> = [
    { key: "ai_agent", label: "FishyHub AI Agent", color: "#0c55b8" },
    { key: "build_journeys", label: "Build Journeys", color: "#26a69a" },
    { key: "holiday_section", label: "Public Holiday", color: "#ef5350" },
    { key: "personalized_section", label: "Personalized", color: "#ab47bc" },
    { key: "smart_bundle", label: "Smart Bundles", color: "#ffa726" },
    { key: "search_bar", label: "Search Bar", color: "#29b6f6" },
    { key: "shop_section", label: "Shop Sections", color: "#66bb6a" },
    { key: "search_filter", label: "Search Filter", color: "#8d6e63" },
    { key: "multiple", label: "Multiple Features", color: "#5c6bc0" },
    { key: "others", label: "Others", color: "#bdbdbd" },
  ];

  get markets(): string[] {
    return this.appStateService.liveMarketCodes();
  }
  /** The market every order and cart series is drawn for; SG by default. */
  market: DashboardMarket = "SG";
  /** The market whose charts are on show: the toggle goes back to it if a switch fails. */
  private loadedMarket: DashboardMarket = "SG";
  /** That market's currency, as the API reports it (from the country table). */
  currency = "SGD";

  // Cart activity is money too, so its value axis carries the market's currency.
  cartChartOptions: ChartConfiguration["options"] = this.cartOptions();

  /** Bound wrapper for the refresh button — see `loadDashboard()`. */
  refreshData = (): void => this.loadDashboard();

  constructor(
    private request: RequestService,
    public appStateService: AppStateService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * The API sends about six months of days and 25 months. Each daily or
   * monthly chart shows the bars that fit (40px a day, 60px a month) and
   * scrolls sideways through the rest with its y-axis and legend fixed,
   * opening at the newest end (scroll-window.plugin.ts).
   */
  readonly dailyScroll = [scrollWindow(40)];
  readonly monthlyScroll = [scrollWindow(60)];

  ngOnInit() {
    // Route is already gated by AuthGaurd (an async check against the
    // `ab_session` cookie) — no client-side localStorage check needed here.
    this.loadDashboard();
  }

  /**
   * The markets behave as one exclusive choice: ticking one unticks the others, and
   * the ticked one cannot be unticked (there is always exactly one market).
   */
  selectMarket(iso2: DashboardMarket, event: Event): void {
    const box = event.target as HTMLInputElement;
    if (iso2 === this.market) {
      box.checked = true;
      return;
    }
    this.market = iso2;
    this.loadDashboard();
  }

  private loadDashboard(): void {
    const requested = this.market;
    const url = `/api/v1/dashboard?iso2=${requested}`;
    // Failed: the toggle goes back to the market whose charts are still on show.
    const failed = () => {
      if (requested !== this.market) return;
      this.market = this.loadedMarket;
      this.snackBar.open(`Could not load the ${requested} dashboard`, "Close", { duration: 4000 });
    };
    this.request.get<IDashboardApiResponse>(url).subscribe({
      next: (res) => {
        // A slower reply for a market the reader has since switched away from
        // must not overwrite the charts of the one now selected.
        if (requested !== this.market) return;
        if (res.result_code !== 1) return failed();
        this.loadedMarket = requested;
        this.applyCurrency(res.data.currency);
        this.processCountryCharts(res.data);
        this.processDailySearches(res.data.dailySearches || []);
        this.processMonthlySearches(res.data.monthlySearches || []);
        this.processDailyCartActivity(res.data.dailyCartActivity || []);
        this.processMonthlyCartActivity(res.data.monthlyCartActivity || []);
        this.processMonthlyOrdersByFeature(res.data.monthlyOrdersByFeature || []);
        this.processActiveUsersByFeature(res.data.activeUsersByFeature || []);
      },
      error: failed,
    });
  }

  /** Re-labels every money axis with the selected market's currency. */
  private applyCurrency(currency: string | undefined): void {
    this.currency = currency || this.market;
    for (const chart of this.countryCharts) {
      if (!chart.money) continue;
      chart.axis = `Sales (${this.currency})`;
      chart.options = this.stackedCountryOptions(chart.axis, chart.money);
    }
    this.cartChartOptions = this.cartOptions();
  }

  private cartOptions(): ChartConfiguration["options"] {
    const options = structuredClone(this.chartOptions) as any;
    options.scales.y.title.text = "Items";
    options.scales.y1.title.text = `Cart value (${this.currency})`;
    return options;
  }

  // -------------------------------------------------------------------------
  // Country-stacked charts
  // -------------------------------------------------------------------------

  /**
   * One colour per country, everywhere.
   *
   * Fixed rather than assigned in order, so SG is the same blue on all eight
   * charts even though the country lists differ between them - a colour that
   * moved between charts would make them impossible to read side by side.
   */
  private readonly COUNTRY_COLOURS: Record<string, string> = {
    SG: "#0c55b8",
    MY: "#26a69a",
    US: "#ef5350",
    ID: "#ffa726",
    TH: "#ab47bc",
    PH: "#29b6f6",
    VN: "#66bb6a",
    AU: "#8d6e63",
    IN: "#5c6bc0",
    HK: "#ec407a",
    CN: "#26c6da",
    JP: "#d4e157",
    Unknown: "#bdbdbd",
    /** Used when the breakdown is unavailable and the chart falls back to totals. */
    All: "#0c55b8",
  };

  /** For a country not in the fixed list; indexed so two new countries differ. */
  private readonly SPARE_COLOURS = [
    "#7e57c2",
    "#ff7043",
    "#78909c",
    "#9ccc65",
    "#5d4037",
    "#00897b",
    "#c2185b",
    "#fbc02d",
  ];

  /** The legend swatch beside a country's checkbox, matching its bar colour. */
  countrySwatch(iso2: string, index: number): string {
    return this.countryColour(iso2, index);
  }

  private countryColour(iso2: string, index: number): string {
    return this.COUNTRY_COLOURS[iso2] ?? this.SPARE_COLOURS[index % this.SPARE_COLOURS.length];
  }

  /**
   * The eight country-split charts, in the order they appear on the page:
   * orders and sales first, then registrations, then active users.
   */
  countryCharts: CountryChart[] = [
    this.newCountryChart(
      "dailyOrders",
      "Daily Orders",
      "Orders",
      false,
      "Confirmed orders per day in the selected market, for about its last six months of days with orders - scroll sideways for older days.",
      true
    ),
    this.newCountryChart(
      "dailySales",
      "Daily Sales",
      "Sales (SGD)",
      true,
      "Confirmed order value (sub-total) per day in the selected market, over the same window.",
      true
    ),
    this.newCountryChart(
      "monthlyOrders",
      "Monthly Orders",
      "Orders",
      false,
      "Confirmed orders per month in the selected market, for its last 25 months with orders - scroll sideways for older months.",
      true
    ),
    this.newCountryChart(
      "monthlySales",
      "Monthly Sales",
      "Sales (SGD)",
      true,
      "Confirmed order value (sub-total) per month in the selected market, over the same window.",
      true
    ),
    this.newCountryChart(
      "dailyRegistrations",
      "Daily Registrations",
      "New users",
      false,
      "New active shopper accounts per day, stacked by the market the account belongs to."
    ),
    this.newCountryChart(
      "monthlyRegistrations",
      "Monthly Registrations",
      "New users",
      false,
      "New active shopper accounts per month, stacked by market."
    ),
    this.newCountryChart(
      "dailyActiveUsers",
      "Daily Active Users (DAU)",
      "Active users",
      false,
      "Distinct signed-in users per day, stacked by market. Counted live from access activity rather than from the DAU snapshots, which hold one total per day and carry no country - so these figures can differ slightly from the snapshot-based ones."
    ),
    this.newCountryChart(
      "monthlyActiveUsers",
      "Monthly Active Users (MAU)",
      "Active users",
      false,
      "Distinct signed-in users per month, stacked by market. Counted live for the same reason as DAU."
    ),
  ];

  private newCountryChart(
    key: string,
    title: string,
    axis: string,
    money: boolean,
    subtitle: string,
    market = false
  ): CountryChart {
    return {
      key,
      title,
      axis,
      money,
      market,
      subtitle,
      rows: [],
      countries: [],
      selected: new Set<string>(),
      data: { labels: [], datasets: [] },
      options: this.stackedCountryOptions(axis, money),
    };
  }

  private format(value: number, money: boolean): string {
    return money
      ? `${this.currency} ${Math.round(value).toLocaleString()}`
      : value.toLocaleString();
  }

  private stackedCountryOptions(axis: string, money: boolean): ChartConfiguration["options"] {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      // Room for the stack total printed above the tallest bar.
      layout: { padding: { top: 26 } },
      plugins: {
        legend: { display: true, position: "top" as const },
        // Labels are configured per dataset in rebuildCountryChart, because the
        // stack total may only be drawn by the topmost one.
        datalabels: { display: false },
        tooltip: {
          callbacks: {
            footer: (items: any[]) =>
              `Total: ${this.format(
                items.reduce((sum, it) => sum + (it.parsed?.y ?? 0), 0),
                money
              )}`,
          },
        },
      } as any,
      scales: {
        x: { stacked: true },
        y: {
          stacked: true,
          type: "linear" as const,
          beginAtZero: true,
          title: { display: true, text: axis },
        },
      },
    };
  }

  private chart(key: string): CountryChart {
    return this.countryCharts.find((c) => c.key === key) as CountryChart;
  }

  /**
   * Fills a chart, ticking every country it found.
   *
   * "Unknown" is sorted last so a real market never sits behind the catch-all.
   */
  private setCountryRows(key: string, rows: CountryRow[]): void {
    const chart = this.chart(key);
    chart.rows = rows;
    chart.countries = [...new Set(rows.map((r) => r.iso2))].sort((a, b) =>
      a === "Unknown" ? 1 : b === "Unknown" ? -1 : a.localeCompare(b)
    );
    chart.selected = new Set(chart.countries);
    this.rebuildCountryChart(chart);
  }

  private rebuildCountryChart(chart: CountryChart): void {
    const labelOf = new Map<string, string>();
    for (const r of chart.rows) if (!labelOf.has(r.dt)) labelOf.set(r.dt, r.label);
    // Period keys are "YYYY-MM-DD" / "YYYY-MM", so a plain sort is chronological.
    const periods = [...labelOf.keys()].sort();

    const active = chart.countries.filter((c) => chart.selected.has(c));
    const byCountry = new Map<string, Map<string, number>>();
    for (const r of chart.rows) {
      if (!chart.selected.has(r.iso2)) continue;
      if (!byCountry.has(r.iso2)) byCountry.set(r.iso2, new Map());
      const series = byCountry.get(r.iso2) as Map<string, number>;
      series.set(r.dt, (series.get(r.dt) ?? 0) + r.value);
    }

    const totals = periods.map((dt) =>
      active.reduce((sum, iso2) => sum + (byCountry.get(iso2)?.get(dt) ?? 0), 0)
    );
    const peak = totals.length ? Math.max(...totals) : 0;
    // A segment shorter than this cannot hold a legible number, so it goes
    // unlabelled and the tooltip carries it instead. Without a floor the small
    // markets print on top of each other at the foot of every bar.
    const floor = peak * 0.06;
    const money = chart.money;

    // With one country the segment IS the stack, so the in-bar number and the
    // total above it would be the same value printed twice; only the total is
    // drawn in that case.
    const single = active.length === 1;

    const datasets = active.map((iso2, i) => {
      const series = byCountry.get(iso2);
      const colour = this.countryColour(iso2, i);
      const topmost = i === active.length - 1;
      return {
        type: "bar" as const,
        label: iso2,
        data: periods.map((dt) => series?.get(dt) ?? 0),
        backgroundColor: colour,
        borderColor: colour,
        borderWidth: 0,
        stack: chart.key,
        datalabels: {
          labels: {
            value: {
              anchor: "center" as const,
              align: "center" as const,
              color: "#ffffff",
              font: { size: 9, weight: "bold" as const },
              formatter: (v: number) => (v > 0 ? this.format(v, money) : ""),
              display: (ctx: any) =>
                !single && floor > 0 && (ctx.dataset.data[ctx.dataIndex] ?? 0) >= floor,
            },
            // The stack total, once per bar: only the topmost dataset draws it,
            // otherwise every segment would print the same number.
            total: topmost
              ? {
                  // Explicit: without it this inherits the display:false set on
                  // the plugin root, and the total silently never draws.
                  display: true,
                  anchor: "end" as const,
                  align: "top" as const,
                  offset: 2,
                  color: "#37474f",
                  font: { size: 10, weight: "bold" as const },
                  // Summed from the bar being drawn, not looked up in `totals`:
                  // the chart holds only the window on show (scroll-window
                  // plugin), so its dataIndex is not a position in `totals`.
                  formatter: (_v: number, ctx: any) => {
                    const total = (ctx.chart.data.datasets as any[]).reduce(
                      (sum: number, d: any, i: number) =>
                        ctx.chart.isDatasetVisible(i)
                          ? sum + (Number(d.data[ctx.dataIndex]) || 0)
                          : sum,
                      0
                    );
                    return total > 0 ? this.format(total, money) : "";
                  },
                }
              : { display: false },
          },
        },
      } as any;
    });

    chart.data = { labels: periods.map((dt) => labelOf.get(dt) ?? dt), datasets };
  }

  isCountrySelected(chart: CountryChart, iso2: string): boolean {
    return chart.selected.has(iso2);
  }

  toggleCountry(chart: CountryChart, iso2: string): void {
    if (chart.selected.has(iso2)) chart.selected.delete(iso2);
    else chart.selected.add(iso2);
    this.rebuildCountryChart(chart);
  }

  selectAllCountries(chart: CountryChart): void {
    chart.selected = new Set(chart.countries);
    this.rebuildCountryChart(chart);
  }

  clearCountries(chart: CountryChart): void {
    chart.selected = new Set<string>();
    this.rebuildCountryChart(chart);
  }

  /**
   * A country breakdown, or a single "All" series built from the plain totals.
   *
   * The breakdowns are fail-soft on the server: if one of them cannot be read
   * the dashboard still draws the chart from the total it has always had,
   * rather than showing an empty card. The fallback's period key is the row's
   * position, which keeps the order the API sent.
   */
  private countryRows<T>(
    breakdown: T[] | undefined,
    read: (row: T) => CountryRow,
    fallback: { rows: any[] | undefined; label: string; value: string; iso2?: string }
  ): CountryRow[] {
    if (breakdown?.length) return breakdown.map(read);
    return (fallback.rows ?? []).map((row, i) => ({
      dt: String(i).padStart(4, "0"),
      label: String(row[fallback.label] ?? ""),
      // An order series is already one market, so its fallback is named after it.
      iso2: fallback.iso2 ?? "All",
      value: Number(row[fallback.value]) || 0,
    }));
  }

  private processCountryCharts(data: IDashboardApiData): void {
    this.setCountryRows(
      "dailyOrders",
      this.countryRows(
        data.dailyOrdersByCountry,
        (r) => ({ dt: r.dt, label: r.date, iso2: r.iso2, value: Number(r.orders) || 0 }),
        { rows: data.daily, label: "created_on", value: "orders", iso2: this.market }
      )
    );
    this.setCountryRows(
      "dailySales",
      this.countryRows(
        data.dailyOrdersByCountry,
        (r) => ({ dt: r.dt, label: r.date, iso2: r.iso2, value: Number(r.sales) || 0 }),
        { rows: data.daily, label: "created_on", value: "sales", iso2: this.market }
      )
    );
    this.setCountryRows(
      "monthlyOrders",
      this.countryRows(
        data.monthlyOrdersByCountry,
        (r) => ({ dt: r.dt, label: r.month, iso2: r.iso2, value: Number(r.orders) || 0 }),
        { rows: data.monthly, label: "month", value: "orders", iso2: this.market }
      )
    );
    this.setCountryRows(
      "monthlySales",
      this.countryRows(
        data.monthlyOrdersByCountry,
        (r) => ({ dt: r.dt, label: r.month, iso2: r.iso2, value: Number(r.sales) || 0 }),
        { rows: data.monthly, label: "month", value: "sales", iso2: this.market }
      )
    );
    this.setCountryRows(
      "dailyRegistrations",
      this.countryRows(
        data.dailyUsersByCountry,
        (r) => ({ dt: r.dt, label: r.date, iso2: r.iso2, value: Number(r.users) || 0 }),
        { rows: data.dailyUsersCount, label: "joined_on", value: "total_daily_users" }
      )
    );
    this.setCountryRows(
      "monthlyRegistrations",
      this.countryRows(
        data.monthlyUsersByCountry,
        (r) => ({ dt: r.dt, label: r.month, iso2: r.iso2, value: Number(r.users) || 0 }),
        { rows: data.monthlyUsersCount, label: "month", value: "monthly_daily_users" }
      )
    );
    this.setCountryRows(
      "dailyActiveUsers",
      this.countryRows(
        data.dailyActiveUsersByCountry,
        (r) => ({ dt: r.dt, label: r.date, iso2: r.iso2, value: Number(r.userCount) || 0 }),
        { rows: data.dailyActiveUsers, label: "date", value: "userCount" }
      )
    );
    this.setCountryRows(
      "monthlyActiveUsers",
      this.countryRows(
        data.monthlyActiveUsersByCountry,
        (r) => ({ dt: r.dt, label: r.month, iso2: r.iso2, value: Number(r.userCount) || 0 }),
        { rows: data.monthlyActiveUsers, label: "month", value: "userCount" }
      )
    );
  }

  processActiveUsersByFeature(data: IActiveUsersByFeatureInfo[]) {
    // Backend already orders by most-used; sort defensively so the bars are always
    // ranked even if the payload order changes.
    const rows = [...data].map((r) => ({
      feature: r.feature,
      userCount: Number(r.userCount) || 0,
    }));
    // Feed and Reels always have a bar, even before anyone has used them this month.
    for (const feature of ["feed", "reels"]) {
      if (!rows.some((r) => r.feature === feature)) rows.push({ feature, userCount: 0 });
    }
    rows.sort((a, b) => b.userCount - a.userCount);

    this.activeUsersByFeatureData = {
      labels: rows.map((r) => this.FEATURE_LABELS[r.feature] ?? r.feature),
      datasets: [
        {
          type: "bar" as const,
          label: "Active users",
          data: rows.map((r) => r.userCount),
          backgroundColor: "rgba(12, 85, 184, 0.75)",
          borderColor: "#0c55b8",
          borderWidth: 1,
        } as any,
      ],
    };
  }

  processMonthlyOrdersByFeature(data: IMonthlyOrdersByFeatureInfo[]) {
    // Preserve month order as returned (already sorted ascending by the backend).
    const monthOrder: string[] = [];
    const monthLabel = new Map<string, string>();
    for (const row of data) {
      if (!monthLabel.has(row.dt)) {
        monthLabel.set(row.dt, row.month);
        monthOrder.push(row.dt);
      }
    }

    // counts[featureKey][dt] = orders
    const counts = new Map<string, Map<string, number>>();
    for (const row of data) {
      const key = this.ORDER_FEATURES.some((f) => f.key === row.feature) ? row.feature : "others";
      if (!counts.has(key)) counts.set(key, new Map());
      const byMonth = counts.get(key)!;
      byMonth.set(row.dt, (byMonth.get(row.dt) ?? 0) + Number(row.orders));
    }

    // One stacked dataset per feature, in fixed order; drop features with no orders
    // across the whole window to keep the legend clean. Missing cells zero-filled.
    const datasets = this.ORDER_FEATURES.filter((f) => counts.has(f.key)).map((f) => {
      const byMonth = counts.get(f.key)!;
      return {
        type: "bar" as const,
        label: f.label,
        data: monthOrder.map((dt) => byMonth.get(dt) ?? 0),
        backgroundColor: f.color,
        borderColor: f.color,
        borderWidth: 0,
        stack: "orders",
      } as any;
    });

    this.monthlyOrdersByFeatureData = {
      labels: monthOrder.map((dt) => monthLabel.get(dt) ?? dt),
      datasets,
    };
  }

  processDailySearches(data: any[]) {
    this.dailySearchesData = {
      labels: data.map((d) => d.date),
      datasets: [
        {
          type: "bar" as const,
          label: "Searches",
          data: data.map((d) => parseInt(d.searchCount, 10)),
          backgroundColor: "rgba(103, 58, 183, 0.7)",
          borderColor: "#673ab7",
          borderWidth: 1,
        },
      ],
    };
  }

  processMonthlySearches(data: any[]) {
    this.monthlySearchesData = {
      labels: data.map((d) => d.month),
      datasets: [
        {
          type: "bar" as const,
          label: "Searches",
          data: data.map((d) => parseInt(d.searchCount, 10)),
          backgroundColor: "rgba(81, 45, 168, 0.7)",
          borderColor: "#512da8",
          borderWidth: 1,
        },
      ],
    };
  }

  processDailyCartActivity(data: any[]) {
    this.dailyCartData = {
      labels: data.map((d) => d.date),
      datasets: [
        {
          type: "bar" as const,
          label: "Items Added",
          data: data.map((d) => parseInt(d.totalItems, 10)),
          backgroundColor: "rgba(0, 150, 136, 0.7)",
          borderColor: "#009688",
          borderWidth: 1,
          yAxisID: "y",
        },
        {
          type: "line" as const,
          label: `Cart Value (${this.currency})`,
          data: data.map((d) => parseFloat(d.totalValue)),
          borderColor: "#ff5722",
          backgroundColor: "rgba(255, 87, 34, 0.1)",
          borderWidth: 2,
          tension: 0.4,
          fill: true,
          yAxisID: "y1",
        },
      ],
    };
  }

  processMonthlyCartActivity(data: any[]) {
    this.monthlyCartData = {
      labels: data.map((d) => d.month),
      datasets: [
        {
          type: "bar" as const,
          label: "Items Added",
          data: data.map((d) => parseInt(d.totalItems, 10)),
          backgroundColor: "rgba(0, 121, 107, 0.7)",
          borderColor: "#00796b",
          borderWidth: 1,
          yAxisID: "y",
        },
        {
          type: "line" as const,
          label: `Cart Value (${this.currency})`,
          data: data.map((d) => parseFloat(d.totalValue)),
          borderColor: "#ff5722",
          backgroundColor: "rgba(255, 87, 34, 0.1)",
          borderWidth: 2,
          tension: 0.4,
          fill: true,
          yAxisID: "y1",
        },
      ],
    };
  }
}
